import React, { useMemo } from 'react';
import { motion, type Variants } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, ShieldCheck } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
} satisfies Variants;

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
} satisfies Variants;

const PAGE_SURFACE = '#f4f7fc';

const SITE_DOMAIN = 'www.manehaghighi.com';

type PrivacyBlock = { title: string; bullets: string[] };

const privacySections: PrivacyBlock[] = [
  {
    title: 'مقدمه و گستره',
    bullets: [
      `این سیاست برای وب‌سایت رسمی «من حقیقی» در آدرس ${SITE_DOMAIN} و زیرسرویس‌های وابسته (در ادامه «سامانه») تنظیم شده است. خدمات شامل محتوای آموزشی آنلاین، دوره‌ها، کارگاه‌ها، پادکست‌ها، ثبت‌نام، خرید، حساب کاربری و پشتیبانی است.`,
      'با استفاده از سامانه شما می‌پذیرید که این مستند توضیح می‌دهد چه اطلاعاتی ممکن است ثبت شود، برای چه هدفی استفاده می‌شود و چطور از آن محافظت می‌شود. در صورت عدم موافقت، از ارائهٔ داده‌های غیرضروری خودداری کنید یا از ثبت‌نام خودداری نمایید؛ برخی خدمات بدون احراز هویت یا اطلاعات تماس ممکن است محدود باشند.',
      'این سیاست با رعایت اصول کلی قوانین جمهوری اسلامی ایران، از جمله قانون تجارت الکترونیکی و الزامات حمایت از حقوق مصرف‌کننده در بستر آنلاین، تدوین شده است. این متن یک معرفی شفاف برای کاربران است و جای قرارداد یا شرایط خدمات تخصصی («قوانین و مقررات») را پر نمی‌کند.',
    ],
  },
  {
    title: 'اطلاعاتی که ممکن است جمع‌آوری شود',
    bullets: [
      'هویت و تماس: نام و نام خانوادگی، شمارهٔ تماس، نشانی ایمیل یا موارد مشابهی که هنگام ثبت‌نام، ورود، تکمیل فرم تماس یا تراکنش ارائه می‌کنید.',
      'تراکنش و آموزش: شناسهٔ سفارش، تاریخ و وضعیت پرداخت، اقلام خریداری‌شده، دسترسی مجاز به محتوای دوره‌ها و در صورت وجود، پیشرفت یادگیری در بستر خود سامانه.',
      'فنی و امنیتی: آدرس اینترنتی (IP)، نوع دستگاه و مرورگر، زبان، زمان مراجعه، کوکی‌ها یا شناسه‌های جلسهٔ مرورگر، گزارش خطاها و رویدادهای امنیتی سمت سرور در حد متعارف.',
      'ارتباط با پشتیبانی: متن پیام‌ها، تیکت‌ها یا مکاتباتی که برای پشتیبانی ارسال می‌کنید.',
    ],
  },
  {
    title: 'اهداف استفاده از اطلاعات',
    bullets: [
      'ایجاد و مدیریت حساب کاربری، ارائهٔ خدمات آموزشی و فروش قانونی محتوا.',
      'پردازش پرداخت، صدور رسید و اعطای دسترسی مجاز به دوره‌ها یا محصولات دیجیتال.',
      'پاسخ‌گویی به درخواست‌ها، پشتیبانی، اطلاع‌رسانی ضروری دربارهٔ تراکنش یا نقض امنیتی حساب.',
      'بهبود عملکرد سامانه، تجربهٔ کاربری در حد معقول، پیشگیری از تقلب و سوءاستفاده، رعایت الزامات قضایی در صورت ارائهٔ دستور معتبر.',
      'ارسال محتوای بازاریابی تنها در صورت رعایت قوانین مربوط و در صورتی که مجوز قانونی یا انتخاب شما ایجاب کند؛ می‌توانید از دریافت پیام‌های غیرضروری خارج از تراکنش خودداری کنید مگر از نظر فنی همان پیام در زمرهٔ خدمت ضروری باشد.',
    ],
  },
  {
    title: 'اشتراک با اشخاص ثالث',
    bullets: [
      'درگاه‌های پرداخت آنلاین برای تکمیل تراکنش؛ این ارائه‌دهندگان صرفاً به داده‌های لازم برای پرداخت دسترسی دارند و ملزم به رعایت الزامات خود هستند.',
      'زیرساخت میزبانی، ارسال پیامک یا ایمیل، تحلیل آماری فنی یا ابزارهای ضد تقلب در صورت استفاده؛ در این موارد تلاش می‌شود قرارداد یا استانداردی که حداقل حفاظت را تضمین می‌کند رعایت شود.',
      'مراجع قضایی یا اداری در چارچوب قانون در صورت ارائهٔ دستور معتبر.',
      'ما اطلاعات شخصی شما را برای بازاریابی شخص ثالث‌ها نمی‌فروشیم.',
    ],
  },
  {
    title: 'ذخیره‌سازی، امنیت و دورهٔ نگهداری',
    bullets: [
      'اقدامات متعارف امنیتی شامل رمزنگاری ارتباط بر بستر HTTPS، کنترل دسترسی سمت سرور و به‌روز بودن نرم‌افزارها در حد معقول به کار گرفته می‌شود؛ با این حال هیچ سیستمی «امنیت مطلق» ندارد.',
      'مسئولیت انتخاب رمز عبور محکم و عدم افشای آن بر عهدهٔ شماست. در صورت مشاهدهٔ فعالیت مشکوک، سریعاً از طریق تماس با ما اطلاع دهید.',
      'داده‌ها تا زمانی که برای ارائهٔ خدمات، تسویهٔ مالی، رسیدگی حقوقی یا رعایت تکالیف قانونی لازم است نگهداری می‌شوند و پس از آن حذف یا غیرقابل شناسایی‌سازی می‌گردند مگر قانون بازهٔ طولانی‌تری الزام کند.',
    ],
  },
  {
    title: 'کوکی‌ها و فناوری‌های مشابه',
    bullets: [
      'سامانه ممکن است از کوکی‌ها یا فضای ذخیرهٔ مرورگر برای نگه‌داشتن جلسهٔ ورود، ترجیح زبان، سبد خرید یا آمار فنی استفاده کند.',
      'می‌توانید ذخیرهٔ کوکی‌ها را از تنظیمات مرورگر محدود کنید؛ در این صورت برخی امکانات مانند باقی‌ماندن لاگین یا سبد خرید ممکن است درست کار نکنند.',
    ],
  },
  {
    title: 'حقوق شما',
    bullets: [
      'درخواست دسترسی یا اصلاح اطلاعات حساب در حد امکان از طریق پیشخوان کاربری یا تماس با پشتیبانی.',
      'درخواست حذف حساب در صورت عدم مانع قانونی؛ توجه داشته باشید حذف دسترسی به محتوای خریداری‌شده یا سوابق مالی الزامی ممکن است هم‌زمان با قوانین تجاری و مالیاتی محدودیت داشته باشد.',
      'اعتبارسنجی هویت پیش از انجام درخواس‌های حساس جهت جلوگیری از سوءاستفاده.',
    ],
  },
  {
    title: 'کودکان و نوجوانان',
    bullets: [
      'خدمات مجموعه عمدتاً مخاطب بزرگسال دارد. استفادهٔ زیر ۱۸ سال پیشنهاد می‌شود تحت نظارت والد یا قیم انجام شود؛ داده‌های تماس کودک نباید بدون رضایت قانونی ارائه شود.',
    ],
  },
  {
    title: 'تغییرات و تماس',
    bullets: [
      'این سیاست می‌تواند به‌روز شود. تاریخ «آخرین به‌روزرسانی» در انتهای صفحه درج می‌شود؛ ادامهٔ استفاده پس از انتشار نسخهٔ جدید می‌تواند به‌معنای علم به تغییر باشد مگر خلاف آن صریحاً مقرر شده باشد.',
      'برای هر پرسش دربارهٔ حریم خصوصی یا حقوق داده، از صفحهٔ «تماس با ما» استفاده کنید یا به ایمیل و تلفن رسمی اعلام‌شده در همان صفحه مراجعه کنید.',
      'برای شرایط حقوقی استفاده از محصولات و قرارداد کاربر، صفحهٔ «قوانین و مقررات» (/terms) مرجع است.',
    ],
  },
];

const LAST_UPDATED_LABEL = '۲۰ اردیبهشت ۱۴۰۵';

const Privacy: React.FC = () => {
  const navigate = useNavigate();

  const assets = useMemo(
    () => ({
      hero: '/assets/sliders/Header-Site-1.jpg',
    }),
    [],
  );

  return (
    <div className="min-h-screen pb-16 pt-4 text-[#0f172a]" style={{ backgroundColor: PAGE_SURFACE }} dir="rtl">
      <div className="mx-auto w-full max-w-[min(1220px,96vw)] px-3 sm:px-5 lg:px-6">
        <nav
          aria-label="مسیر دسترسی"
          className="mb-3 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-slate-500 sm:text-xs"
        >
          <Link to="/" className="font-medium transition hover:text-[#3A68C2]">
            خانه
          </Link>
          <span className="text-slate-400 opacity-80">›</span>
          <span className="font-semibold text-slate-700">حریم خصوصی</span>
        </nav>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-6 overflow-hidden rounded-2xl border border-slate-200/85 bg-white shadow-[0_2px_28px_-14px_rgba(58,104,194,0.18),0_8px_32px_-24px_rgba(15,23,42,0.08)] sm:rounded-[1.25rem]"
        >
          <div className="absolute inset-0">
            <img src={assets.hero} alt="" className="h-full w-full object-cover opacity-[0.12]" aria-hidden />
            <div className="absolute inset-0 bg-gradient-to-br from-[#f9fbff] via-white to-[#eef4ff]" />
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-emerald-400/15 blur-3xl" />
          </div>
          <div className="relative px-4 py-5 sm:px-6 sm:py-6">
            <motion.div variants={stagger} initial="hidden" animate="visible" className="space-y-4">
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-700" />
                <span className="text-[11px] font-bold text-emerald-900 sm:text-xs">حریم خصوصی و داده‌های شخصی</span>
              </motion.div>
              <motion.h1
                variants={fadeUp}
                className="text-[1.35rem] font-extrabold leading-tight text-[#0f172a] sm:text-2xl md:text-3xl"
              >
                شفافیت دربارهٔ{' '}
                <span className="text-emerald-700">اطلاعات شما</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="max-w-3xl text-sm leading-7 text-slate-600 sm:text-[15px]">
                ما در «من حقیقی» داده‌های شما را صرفاً برای ارائهٔ تجربهٔ ایمن آموزشی، پرداخت و پشتیبانی استفاده می‌کنیم. این صفحهٔ خلاصهٔ سیاست حریم خصوصی است؛ برای شرایط حقوقی خدمات، به{' '}
                <Link to="/terms" className="font-semibold text-[#3A68C2] underline-offset-2 hover:underline">
                  قوانین و مقررات
                </Link>{' '}
                مراجعه کنید.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white/90 px-3 py-2 text-[11px] font-semibold text-slate-700 sm:text-xs">
                  <Lock className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                  انتشار: {LAST_UPDATED_LABEL}
                </span>
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-2.5">
                <button
                  type="button"
                  onClick={() => navigate('/contact')}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#3A68C2] px-4 py-2.5 text-xs font-bold text-white shadow-sm transition hover:bg-[#345daf] sm:px-5 sm:py-3 sm:text-sm"
                >
                  تماس دربارهٔ حریم خصوصی
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <Link
                  to="/terms"
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-semibold text-slate-700 transition hover:border-[#3A68C2]/35 hover:text-[#2f5599] sm:px-5 sm:py-3 sm:text-sm"
                >
                  قوانین و مقررات
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        <article className="space-y-5 rounded-2xl border border-slate-200/85 bg-white px-4 py-6 shadow-sm sm:px-6 sm:py-8">
          <p className="border-b border-slate-100 pb-4 text-xs leading-6 text-slate-500 sm:text-sm">
            آخرین به‌روزرسانی نسخهٔ متن: <strong className="font-bold text-slate-700">{LAST_UPDATED_LABEL}</strong>
          </p>

          <div className="space-y-8">
            {privacySections.map((sec, i) => (
              <section key={sec.title} className="scroll-mt-24">
                <h2 className="mb-3 flex items-baseline gap-2 text-base font-extrabold text-[#0f172a] sm:text-lg">
                  <span className="inline-flex h-8 min-w-[2rem] items-center justify-center rounded-lg bg-[#3A68C2]/10 text-xs font-black text-[#274a9c] sm:text-sm">
                    {i + 1}
                  </span>
                  {sec.title}
                </h2>
                <ul className="list-none space-y-2.5 pr-0 text-sm leading-7 text-slate-600 sm:text-[15px]">
                  {sec.bullets.map((line, bi) => (
                    <li
                      key={`${sec.title}-${bi}`}
                      className="relative pr-4 before:absolute before:right-0 before:top-[0.55em] before:h-1.5 before:w-1.5 before:rounded-full before:bg-[#3A68C2]/50"
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <div className="mt-8 rounded-xl border border-slate-100 bg-slate-50/80 px-4 py-4 text-center text-xs leading-6 text-slate-600 sm:text-sm">
            در صورت تناقض میان این صفحه و متن قراردادی یا اعلان‌های رسمی دیگر، نسخهٔ رسمی و به‌روز منتشرشده توسط «من حقیقی» بر اساس مورد، ملاک است.
          </div>
        </article>
      </div>
    </div>
  );
};

export default Privacy;
